import { graphqlPost } from "api/graphqlClient";
import { Markdown } from "components/Form/components/Markdown/Markdown";
import { CustomObjectProvisioningState } from "graphql/generated/graphql";
import { isEqual } from "lodash";
import { useCallback, useEffect, useRef, useState } from "react";
import { DashboardRecordLink } from "../DashboardRecordLink";
import { DashboardLiveWidgetShell } from "../DashboardLiveWidgetShell";
import { describeWidgetFetchError } from "../dashboardWidgetError";
import { SingleRecordWidgetConfig, WidgetComponentProps } from "../types";
import { useDashboardLiveRefresh } from "../useDashboardLiveRefresh";

interface SingleRecord {
    id: string;
    name: string;
    created_at?: string;
    modified_at?: string;
    description?: string;
    provisioning_state?: CustomObjectProvisioningState;
    value?: Record<string, any> | null;
}

const POLL_INTERVAL_MS = 10000;

/**
 * `single_record` widget — shows one record by filter (first match, ordered by
 * `order` from config). Useful for "latest push", "newest build", etc.
 *
 * Re-uses the same config shape as `record_list` (same type + filter + order) but
 * queries with `limit: 1` and renders a detail card rather than a list row.
 */
export const SingleRecordWidget = ({
    widget,
    config,
}: WidgetComponentProps<SingleRecordWidgetConfig>) => {
    const [record, setRecord] = useState<SingleRecord | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [isLoading, setLoading] = useState(true);
    const isMounted = useRef(true);
    // The last record committed to state — used to skip a no-op re-render when a
    // `live` re-poll returns the identical record (see fetchRecord).
    const prevRecordRef = useRef<SingleRecord | null | undefined>(undefined);

    const recordType = config?.record_type;
    const apiType = typeof recordType === "string" ? recordType.toLowerCase() : "";
    const order = config?.order ?? null;
    const live = config?.live ?? false;
    const filter =
        config?.filter == null
            ? null
            : typeof config.filter === "string"
            ? config.filter
            : JSON.stringify(config.filter);

    const markdownField = config?.markdown_field;
    const isCustomObject = apiType === "custom_object";
    // For CUSTOM_OBJECT tiles we always fetch provisioning_state (for the record
    // link's status dot); we additionally fetch the JSON `value` only when a
    // markdown_field is configured, so the default card path stays byte-light.
    const extraFields = isCustomObject
        ? `provisioning_state${markdownField ? "\n                value" : ""}`
        : "";

    const fetchRecord = useCallback(async (): Promise<boolean> => {
        if (!apiType) {
            if (isMounted.current) {
                setError("Widget config is missing a record_type.");
                setLoading(false);
            }
            return false;
        }
        const listField = `${apiType}_plain_list`;
        const query = `query DashboardSingleRecord($order: String, $filter: String) {
            ${listField}(limit: 1, order: $order, filter: $filter) {
                id
                name
                created_at
                modified_at
                description
                ${extraFields}
            }
        }`;
        try {
            const response = await graphqlPost({ query, variables: { order, filter } });
            const list = response?.data?.[listField];
            const next: SingleRecord | null =
                Array.isArray(list) && list.length > 0 ? list[0] : null;
            // Skip the re-render (and the lastUpdatedAt bump) when a `live`
            // re-poll returns the identical record — otherwise every poll
            // re-renders the widget with byte-for-byte the same data.
            const changed = !isEqual(prevRecordRef.current, next);
            if (isMounted.current) {
                if (changed) {
                    prevRecordRef.current = next;
                    setRecord(next);
                }
                setError(null);
                setLoading(false);
            }
            return changed;
        } catch (err: any) {
            if (isMounted.current) {
                setError(describeWidgetFetchError(err, "Failed to load record."));
                setLoading(false);
            }
            return false;
        }
    }, [apiType, order, filter, extraFields]);

    const { lastUpdatedAt, refresh, isRefreshing } = useDashboardLiveRefresh({
        live,
        pollIntervalMs: POLL_INTERVAL_MS,
        fetch: fetchRecord,
    });

    useEffect(() => {
        isMounted.current = true;
        return () => {
            isMounted.current = false;
        };
    }, []);

    useEffect(() => {
        setLoading(true);
    }, [fetchRecord]);

    let content;
    // Keep the last record on a failed re-fetch; surface the error inline on the
    // live bar instead of blanking.
    if (error && !record) {
        content = <div className="DashboardWidget__error">{error}</div>;
    } else if (isLoading) {
        content = <div className="DashboardWidget__empty">Loading…</div>;
    } else if (!record) {
        content = <div className="DashboardWidget__empty">No matching record.</div>;
    } else if (markdownField && isCustomObject) {
        // Markdown-body mode: surface a computed markdown field of the CO's JSON
        // `value` (e.g. the throttle-log weekly `summary`) as the tile body.
        const raw = record.value?.[markdownField];
        const markdownString = typeof raw === "string" ? raw : "";
        content = markdownString.trim() ? (
            <div className="DashboardSingleRecord DashboardSingleRecord--markdown">
                <Markdown
                    markdownString={markdownString}
                    uniqueKey={widget.uid}
                    canCopy={false}
                />
            </div>
        ) : (
            <div className="DashboardWidget__empty">
                {`Record has no "${markdownField}" content.`}
            </div>
        );
    } else {
        content = (
            <div className="DashboardSingleRecord">
                <div className="DashboardSingleRecord__name">
                    {recordType ? (
                        <DashboardRecordLink
                            id={record.id}
                            name={record.name}
                            recordType={recordType}
                            provisioningState={record.provisioning_state}
                        />
                    ) : (
                        record.name
                    )}
                </div>
                {record.description && (
                    <p className="DashboardSingleRecord__description">{record.description}</p>
                )}
                {record.modified_at && (
                    <span className="DashboardSingleRecord__meta">
                        Modified: {new Date(record.modified_at).toLocaleString()}
                    </span>
                )}
            </div>
        );
    }

    return (
        <DashboardLiveWidgetShell
            live={live}
            lastUpdatedAt={lastUpdatedAt}
            onRefresh={refresh}
            isRefreshing={isRefreshing}
            error={error}
        >
            {content}
        </DashboardLiveWidgetShell>
    );
};
